import React from 'react';
import { ListingCard } from '@mts/ui/components/listing-card';
import { useListingCardImpression } from '../../hooks/useListingCardImpression';
import { trackVisitorEvent } from '../../services/visitorTracking';
import {
  LISTING_CARD_CLICK_EVENT,
  buildListingCardAnalyticsMetadata,
  normalizeAnalyticsListingId,
} from '../../utils/listingCardAnalytics';

function isNestedInteractiveClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return false;

  const interactive = target.closest('a,button,input,select,textarea,[role="button"]');
  return Boolean(interactive && interactive !== event.currentTarget);
}

function TrackableListingCard({
  listingId,
  context,
  section,
  index,
  variant,
  tier,
  listing,
  targetPath,
  onNavigate,
  analyticsEnabled = true,
  className,
  wrapperClassName,
  ...props
}) {
  const normalizedListingId = normalizeAnalyticsListingId(listingId || listing?.id);
  const analyticsSection = section || context;
  const analyticsContext = context || section;
  const presentationListing = listing
    ? {
        ...listing,
        href: targetPath || undefined,
        url: undefined,
        publicUrl: undefined,
        slug: undefined,
      }
    : listing;

  const impressionRef = useListingCardImpression({
    listingId: normalizedListingId,
    context: analyticsContext,
    section: analyticsSection,
    index,
    tier,
    variant,
    enabled: analyticsEnabled && Boolean(normalizedListingId),
  });

  const handlePrimaryClick = () => {
    if (analyticsEnabled && normalizedListingId) {
      trackVisitorEvent({
        eventType: LISTING_CARD_CLICK_EVENT,
        listingId: normalizedListingId,
        metadata: buildListingCardAnalyticsMetadata({
          context: analyticsContext,
          section: analyticsSection,
          variant,
          index,
          tier,
          targetPath,
          pathname: typeof window !== 'undefined' ? window.location.pathname : '/',
        }),
      });
    }

    onNavigate?.();
  };

  const handlePrimaryLinkClick = (event) => {
    const anchor = event.target.closest?.('a[href]');
    if (!anchor || anchor.getAttribute('href') !== targetPath || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (onNavigate) event.preventDefault();
    handlePrimaryClick();
  };

  const handleWrapperClick = (event) => {
    if (!onNavigate || isNestedInteractiveClick(event)) {
      return;
    }

    handlePrimaryClick();
  };

  const handleWrapperKeyDown = (event) => {
    if (!onNavigate || event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }

    event.preventDefault();
    handlePrimaryClick();
  };
  const cardClassName = [
    className,
    onNavigate && variant === 'row' ? 'hover:bg-slate-50' : '',
    onNavigate && variant !== 'row' ? 'hover:-translate-y-0.5 hover:shadow-md' : '',
  ].filter(Boolean).join(' ') || undefined;

  return (
    <div
      ref={impressionRef}
      role={onNavigate && !targetPath ? 'button' : undefined}
      tabIndex={onNavigate && !targetPath ? 0 : undefined}
      onClickCapture={handlePrimaryLinkClick}
      onClick={handleWrapperClick}
      onKeyDown={handleWrapperKeyDown}
      className={[
        onNavigate ? 'block cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary' : '',
        wrapperClassName || '',
      ].filter(Boolean).join(' ') || undefined}
    >
      <ListingCard
        variant={variant}
        tier={tier}
        listing={presentationListing}
        className={cardClassName}
        {...props}
      />
    </div>
  );
}

export default TrackableListingCard;
